import { z } from 'zod';

/**
 * Reading Core's content-graph envelope (`POST /api/v1/content/search`, the feed): matched
 * roots plus every hydrated node and edge. Only the fields the routine needs are typed;
 * everything else stays opaque so Core can grow the envelope without breaking the app.
 */
/** Who a node came from, as Core hydrates it (`contributors`): enough for a name and a photo. */
const contributorSchema = z.object({
  uuid: z.string(),
  actorType: z.string(),
  name: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  profileImage: z
    .object({ small: z.object({ url: z.string().nullish() }).nullish() })
    .passthrough()
    .nullish(),
});
export type GraphContributor = z.infer<typeof contributorSchema>;

const nodeSchema = z.object({
  uuid: z.string(),
  lineageUuid: z.string(),
  contentTypeUuid: z.string(),
  value: z.unknown().optional(),
  occurredAt: z.string().nullish(),
  createdAt: z.string().nullish(),
  contributors: z.object({ nodes: z.array(z.unknown()) }).nullish(),
});
export type GraphNode = z.infer<typeof nodeSchema>;

/**
 * `AUTHORED_BY` / `SHARED_WITH` edges from a node to an actor. A `GROUP_MEMBER` audience is
 * one person (`uuid`) acting in a group (`scopeUuid`).
 */
const actorEdgeSchema = z.object({
  contentUuid: z.string(),
  edgeType: z.string(),
  audience: z.object({
    principalType: z.string(),
    uuid: z.string().nullish(),
    scopeUuid: z.string().nullish(),
  }),
});
export type GraphActorEdge = z.infer<typeof actorEdgeSchema>;

const edgeSchema = z.object({
  fromUuid: z.string(),
  toUuid: z.string(),
  fromLineageUuid: z.string().nullish(),
  toLineageUuid: z.string().nullish(),
  edgeType: z.string(),
  position: z.number().nullish(),
});
export type GraphEdge = z.infer<typeof edgeSchema>;

const envelopeSchema = z.object({
  data: z.object({
    contentSearch: z.object({
      rootContentUuids: z.array(z.string()),
      totalCount: z.number().optional(),
      contentGraph: z.object({
        contentNodes: z.array(z.unknown()),
        contentEdges: z.array(z.unknown()),
        contentActorEdges: z.array(z.unknown()).optional(),
      }),
    }),
  }),
});

export interface ContentGraph {
  roots: string[];
  totalCount: number;
  nodes: Map<string, GraphNode>;
  edges: GraphEdge[];
  actorEdges: GraphActorEdge[];
}

/** Parse a search response; unreadable nodes or edges are dropped, never fatal. */
export function readGraph(response: unknown): ContentGraph {
  const envelope = envelopeSchema.parse(response).data.contentSearch;
  return graphFrom(
    envelope.rootContentUuids,
    envelope.totalCount ?? envelope.rootContentUuids.length,
    envelope.contentGraph,
  );
}

/** `GET /api/v1/content/{lineageUuid}`: one node and everything below it, its own envelope. */
const treeEnvelopeSchema = z.object({
  data: z.object({
    content: z.object({
      contentNodes: z.array(z.unknown()),
      contentEdges: z.array(z.unknown()),
      contentActorEdges: z.array(z.unknown()).optional(),
    }),
  }),
});

/** Parse a read by lineage, like `readGraph`; it has no roots list. */
export function readTree(response: unknown): ContentGraph {
  return graphFrom([], 0, treeEnvelopeSchema.parse(response).data.content);
}

function graphFrom(
  roots: string[],
  totalCount: number,
  raw: { contentNodes: unknown[]; contentEdges: unknown[]; contentActorEdges?: unknown[] },
): ContentGraph {
  const nodes = new Map<string, GraphNode>();
  for (const item of raw.contentNodes) {
    const parsed = nodeSchema.safeParse(item);
    if (parsed.success) nodes.set(parsed.data.uuid, parsed.data);
  }
  const edges: GraphEdge[] = [];
  for (const item of raw.contentEdges) {
    const parsed = edgeSchema.safeParse(item);
    if (parsed.success) edges.push(parsed.data);
  }
  const actorEdges: GraphActorEdge[] = [];
  for (const item of raw.contentActorEdges ?? []) {
    const parsed = actorEdgeSchema.safeParse(item);
    if (parsed.success) actorEdges.push(parsed.data);
  }
  return { roots, totalCount, nodes, edges, actorEdges };
}

/**
 * The person who authored a node: its `AUTHORED_BY` edge, then that actor among the
 * contributors. A person writing as a member of a group (`GROUP_MEMBER`) is still that person.
 */
export function authorOf(graph: ContentGraph, node: GraphNode): GraphContributor | undefined {
  const authored = graph.actorEdges.find(
    (edge) =>
      edge.contentUuid === node.uuid &&
      edge.edgeType === 'AUTHORED_BY' &&
      (edge.audience.principalType === 'USER' || edge.audience.principalType === 'GROUP_MEMBER'),
  );
  const contributors = (node.contributors?.nodes ?? []).flatMap((raw) => {
    const parsed = contributorSchema.safeParse(raw);
    return parsed.success ? [parsed.data] : [];
  });
  if (authored?.audience.uuid) {
    const match = contributors.find((c) => c.uuid === authored.audience.uuid);
    if (match) return match;
    return { uuid: authored.audience.uuid, actorType: 'USER' };
  }
  return contributors.find((c) => c.actorType === 'USER');
}

/** The `jsonValue` slot of a node's value (Core stores JSON leaves as `{ jsonValue }`). */
export function jsonValueOf(node: GraphNode): unknown {
  const value = node.value;
  if (value && typeof value === 'object' && 'jsonValue' in value) {
    return (value as { jsonValue: unknown }).jsonValue;
  }
  return undefined;
}

/** Nodes CONTAINED in `uuid`, in edge position order. */
export function childrenOf(graph: ContentGraph, uuid: string): GraphNode[] {
  return graph.edges
    .filter((edge) => edge.edgeType === 'CONTAINS' && edge.fromUuid === uuid)
    .sort((x, y) => (x.position ?? 0) - (y.position ?? 0))
    .flatMap((edge) => {
      const node = graph.nodes.get(edge.toUuid);
      return node ? [node] : [];
    });
}

/** Lineage uuids the node INCLUDES (what library item an attempt used). */
export function includedLineagesOf(graph: ContentGraph, uuid: string): string[] {
  return graph.edges
    .filter((edge) => edge.edgeType === 'INCLUDES' && edge.fromUuid === uuid)
    .flatMap((edge) => (edge.toLineageUuid ? [edge.toLineageUuid] : []));
}
